import { useSuspenseQueries } from '@tanstack/react-query';
import c from 'classnames';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { Category } from '@repo/types';
import { groupAndSortSubmetrics } from '@repo/utils';
import {
  Accordion,
  ContainerSizes,
  Icon,
  PageContainer,
  Typography,
} from 'components';
import { getActionPlanItemsQuery } from 'services/actionPlan/actionPlanData';
import { getEvaluationsQuery } from 'services/insights/useSubMetricEvaluations';
import { InsightMetric } from 'types/Insights';
import {
  MetricCard,
  SubMetricRow,
} from 'views/Results/Insights/CategoryMetrics/CategoryMetrics';
import { CategoryMetricsFilter } from 'views/Results/Insights/CategoryMetrics/CategoryMetricsFilter';
import {
  EvaluationGroupsSummary,
  getEvaluationIconFromColor,
} from 'views/Results/Insights/EvaluationGroupsSummary/EvaluationGroupsSummary';

import TwoColumnGrid from 'components/TwoColumnGrid/TwoColumnGrid';
import CategoryMetricsCheckboxFilter, {
  HEALTH_INSIGHTS_OPTIONS,
} from './CategoryMetricsCheckboxFilter';
import { useAnalytics } from 'contexts/analytics/AnalyticsProvider';
import { useAnalyticsSource } from 'contexts/analytics/useAnalyticsSource';

import Breadcrumb from 'components/Breadcrumb/Breadcrumb';
import useKeyInsightsSections from 'services/insights/useKeyInsightsSections';
import styles from './CategoryMetrics.module.css';
import { useAccordionOpenStates } from 'hooks/useAccordionOpenStates';
import { useCategoryMetricsV2Filters } from './hooks/useCategoryMetricsV2Filters';
import {
  getSectionPriorityByTitle,
  orderCategoriesBySectionPriority,
} from './utils/orderCategoriesBySectionPriority';

const SingleMetric = ({
  count,
  color,
  styleClass,
}: {
  count: number;
  color: string;
  styleClass: string;
}) => {
  if (count > 0) {
    return (
      <div className='flex gap-1'>
        <Icon
          name={getEvaluationIconFromColor(color)}
          size='s'
          className={styleClass}
        />
        <Typography variant='body-s' className='my-auto'>
          {count}
        </Typography>
      </div>
    );
  }
  return null;
};

// Insights V2
export const AllMetricsTwoColumnsLayout = () => {
  const { kitId: currentKitId } = useParams<{ kitId: string }>();
  const { trackEvent } = useAnalytics();
  const allMetricsSource = useAnalyticsSource();
  useEffect(() => {
    if (!currentKitId) return;
    trackEvent('All Metrics Viewed', { source: allMetricsSource });
  }, [currentKitId, allMetricsSource, trackEvent]);
  const [{ data: submetrics }] = useSuspenseQueries({
    queries: [
      getEvaluationsQuery(currentKitId),
      getActionPlanItemsQuery(currentKitId),
    ],
  });

  const { data: keyInsightsSections } = useKeyInsightsSections(currentKitId);

  const sectionPriorityByTitle = useMemo(
    () => getSectionPriorityByTitle(keyInsightsSections),
    [keyInsightsSections],
  );

  // Get all available options
  const allHealthInsights = HEALTH_INSIGHTS_OPTIONS.map(o => o.value);
  const allCategories = useMemo(() => {
    return groupAndSortSubmetrics(submetrics).map(category => category.name);
  }, [submetrics]);

  const { healthInsightsFilters, categoryFilters, handleFiltersChange } =
    useCategoryMetricsV2Filters({
      allCategories,
      allHealthInsights,
    });

  const { groupedSubmetrics, healthInsightsCounts } = useMemo(() => {
    const evaluationColorMap: Record<string, string> = {
      red: 'needs_support',
      orange: 'needs_improvement',
      grey: 'okay_results',
      green: 'great_results',
    };

    const newHealthInsightsCounts = {
      great_results: 0,
      okay_results: 0,
      needs_improvement: 0,
      needs_support: 0,
    };

    submetrics.forEach((submetric: InsightMetric) => {
      const evaluationName =
        evaluationColorMap[submetric.evaluation_color ?? ''];
      if (evaluationName) {
        newHealthInsightsCounts[
          evaluationName as keyof typeof newHealthInsightsCounts
        ]++;
      }
    });

    const newGroupedSubmetrics = groupAndSortSubmetrics(submetrics).map(
      (category: Category) => {
        const newCategory = { ...category };
        newCategory.orderedMetrics = category.orderedMetrics.map(metric => {
          const newMetric = { ...metric };
          newMetric.orderedSubmetrics = metric.orderedSubmetrics.map(
            submetric => {
              const evaluationName =
                evaluationColorMap[submetric.evaluation_color ?? ''];

              // Special case: if BOTH filters are empty, show nothing
              const bothFiltersEmpty =
                categoryFilters.length === 0 &&
                healthInsightsFilters.length === 0;

              if (bothFiltersEmpty) {
                return {
                  ...submetric,
                  includedByFilters: false,
                };
              }

              // If one section is empty, treat it as "show all" for that dimension
              const isCategoryIncluded =
                categoryFilters.length === 0 ||
                categoryFilters.includes(category.name);

              const isHealthInsightIncluded =
                healthInsightsFilters.length === 0 ||
                (evaluationName &&
                  healthInsightsFilters.includes(evaluationName));

              return {
                ...submetric,
                includedByFilters: !!(
                  isCategoryIncluded && isHealthInsightIncluded
                ),
              };
            },
          );
          return newMetric;
        });
        return newCategory;
      },
    );

    return {
      groupedSubmetrics: newGroupedSubmetrics,
      healthInsightsCounts: newHealthInsightsCounts,
    };
  }, [submetrics, healthInsightsFilters, categoryFilters]);

  const orderedGroupedSubmetrics = useMemo(
    () =>
      orderCategoriesBySectionPriority(
        groupedSubmetrics,
        sectionPriorityByTitle,
      ),
    [groupedSubmetrics, sectionPriorityByTitle],
  );

  const accordionOpenStatesKey = useMemo(
    () => (currentKitId ? `accordionOpenStates-${currentKitId}` : null),
    [currentKitId],
  );

  const accordionIds = useMemo(
    () => orderedGroupedSubmetrics.map(category => category.name),
    [orderedGroupedSubmetrics],
  );

  const trackAccordionToggle = useCallback(
    (categoryName: string, isOpen: boolean) => {
      trackEvent('Category Drawer Toggled', {
        drawer_name: categoryName,
        state: isOpen ? 'open' : 'closed',
      });
    },
    [trackEvent],
  );

  const { accordionOpenStates, handleAccordionToggle } = useAccordionOpenStates(
    {
      storageKey: accordionOpenStatesKey,
      accordionIds,
      defaultOpen: true,
      onToggle: trackAccordionToggle,
    },
  );

  if (!currentKitId) {
    return <></>;
  }

  return (
    <PageContainer size={ContainerSizes.LG} className={styles.pageContainer}>
      <Breadcrumb className={styles.breadcrumb} />
      <div className='mt-2'>
        <Typography variant='heading-2xl' medium>
          All Insights
        </Typography>
      </div>
      <TwoColumnGrid>
        <TwoColumnGrid.Slim isSticky>
          <CategoryMetricsCheckboxFilter
            healthInsightsFilters={healthInsightsFilters}
            categoryFilters={categoryFilters}
            groupedSubmetrics={orderedGroupedSubmetrics}
            healthInsightsCounts={healthInsightsCounts}
            onFiltersChange={handleFiltersChange}
            keyInsightsSections={keyInsightsSections}
          />
        </TwoColumnGrid.Slim>
        <TwoColumnGrid.Wide>
          {orderedGroupedSubmetrics.map((category: Category) => {
            const emptyCategory = !category.orderedMetrics.some(
              metric =>
                metric.orderedSubmetrics.filter(
                  submetric => submetric.includedByFilters,
                ).length,
            );

            const categoryCounts = {
              green: 0,
              orange: 0,
              grey: 0,
              red: 0,
            };

            category.orderedMetrics.forEach(metric => {
              metric.orderedSubmetrics
                .filter(submetric => submetric.includedByFilters)
                .forEach(submetric => {
                  if (submetric.evaluation_color) {
                    categoryCounts[
                      submetric.evaluation_color as keyof typeof categoryCounts
                    ]++;
                  }
                });
            });

            return (
              <Accordion
                key={category.name}
                isOpen={accordionOpenStates[category.name] ?? !emptyCategory}
                onToggle={isOpen =>
                  handleAccordionToggle(category.name, isOpen)
                }
                header={
                  <div className='flex justify-between w-100 gap-2 pr-4'>
                    <Typography
                      variant='heading-l'
                      className={styles.categoryTitle}
                    >
                      {category.name}
                    </Typography>
                    <div className='flex gap-1'>
                      <SingleMetric
                        count={categoryCounts.red}
                        color='red'
                        styleClass={styles.red}
                      />
                      <SingleMetric
                        count={categoryCounts.orange}
                        color='orange'
                        styleClass={styles.orange}
                      />
                      <SingleMetric
                        count={categoryCounts.grey}
                        color='grey'
                        styleClass={styles.grey}
                      />
                      <SingleMetric
                        count={categoryCounts.green}
                        color='green'
                        styleClass={styles.green}
                      />
                    </div>
                  </div>
                }
                className={styles.metricAccordion}
              >
                {emptyCategory && (
                  <Typography variant='body-s' className={styles.emptyCategory}>
                    No metrics matching your active filters.
                  </Typography>
                )}
                {category.orderedMetrics.map(metric => {
                  const filteredSubmetrics = metric.orderedSubmetrics.filter(
                    submetric => submetric.includedByFilters,
                  );

                  if (!filteredSubmetrics.length) return null;
                  return (
                    <div
                      key={`metric_${metric.name}`}
                      className={styles.categoryMetric}
                    >
                      <Typography
                        variant='heading-s'
                        className={styles.metricTitle}
                      >
                        {metric.name}
                      </Typography>
                      <MetricCard key={`card_${metric.name}`}>
                        {filteredSubmetrics.map(subMetric => (
                          <SubMetricRow
                            key={`${metric.name}_${subMetric.name}`}
                            kitId={currentKitId}
                            subMetric={subMetric}
                          />
                        ))}
                      </MetricCard>
                    </div>
                  );
                })}
              </Accordion>
            );
          })}
        </TwoColumnGrid.Wide>
      </TwoColumnGrid>
    </PageContainer>
  );
};

// Insights V1
export const AllMetrics = () => {
  const { kitId: currentKitId } = useParams<{ kitId: string }>();
  const { trackEvent } = useAnalytics();
  const allMetricsSource = useAnalyticsSource();
  useEffect(() => {
    if (!currentKitId) return;
    trackEvent('All Metrics Viewed', { source: allMetricsSource });
  }, [currentKitId, allMetricsSource, trackEvent]);
  const [{ data: submetrics }] = useSuspenseQueries({
    queries: [
      getEvaluationsQuery(currentKitId),
      getActionPlanItemsQuery(currentKitId),
    ],
  });
  const [evaluationFilters, setEvaluationFilters] = useState<string[]>([]);
  const [accordionOpenStates, setAccordionOpenStates] = useState<
    Record<string, boolean>
  >({});

  const sessionStorageKey = useMemo(
    () => (currentKitId ? `allMetricsAccordionStates_${currentKitId}` : null),
    [currentKitId],
  );

  // Save accordion states to sessionStorage when they change
  useEffect(() => {
    if (sessionStorageKey && Object.keys(accordionOpenStates).length > 0) {
      sessionStorage.setItem(
        sessionStorageKey,
        JSON.stringify(accordionOpenStates),
      );
    }
  }, [accordionOpenStates, sessionStorageKey]);

  const handleAccordionToggle = useCallback(
    (categoryName: string, isOpen: boolean) => {
      trackEvent('Category Drawer Toggled', {
        drawer_name: categoryName,
        state: isOpen ? 'open' : 'closed',
      });
      setAccordionOpenStates(prevStates => ({
        ...prevStates,
        [categoryName]: isOpen,
      }));
    },
    [trackEvent],
  );
  const isIncludedByFilter = useCallback(
    (submetric: InsightMetric) =>
      evaluationFilters.length === 0 ||
      evaluationFilters.includes(submetric.evaluation_color ?? ''),
    [evaluationFilters],
  );

  const groupedSubMetrics = useMemo(() => {
    const groupedSubmetrics = groupAndSortSubmetrics(submetrics).map(
      (category: Category) => {
        category.orderedMetrics = category.orderedMetrics.map(metric => {
          metric.orderedSubmetrics = metric.orderedSubmetrics.map(
            submetric => ({
              ...submetric,
              includedByFilters: !!isIncludedByFilter(submetric),
            }),
          );
          return metric;
        });
        return category;
      },
    );
    return groupedSubmetrics;
  }, [submetrics, isIncludedByFilter]);

  // Load accordion states from sessionStorage on mount
  useEffect(() => {
    if (!sessionStorageKey) return;

    const initializeDefaultStates = () => {
      const initialStates: Record<string, boolean> = {};
      groupedSubMetrics.forEach(category => {
        const emptyCategory = !category.orderedMetrics.some(
          metric =>
            metric.orderedSubmetrics.filter(
              submetric => submetric.includedByFilters,
            ).length,
        );
        initialStates[category.name] = !emptyCategory;
      });
      setAccordionOpenStates(initialStates);
    };

    const savedStates = sessionStorage.getItem(sessionStorageKey);
    if (savedStates) {
      try {
        setAccordionOpenStates(JSON.parse(savedStates));
      } catch (error) {
        console.error(
          'Error parsing accordion states from sessionStorage:',
          error,
        );
        initializeDefaultStates();
      }
    } else {
      initializeDefaultStates();
    }
  }, [sessionStorageKey, groupedSubMetrics, isIncludedByFilter]);

  if (!submetrics?.length || !currentKitId) return <></>;

  return (
    <PageContainer
      size={ContainerSizes.MD}
      className={c(styles.allMetrics, 'pt-3')}
    >
      <div className={styles.header}>
        <div className='flex flex-column gap-2'>
          <Typography variant='heading-2xl' medium>
            All Insights
          </Typography>
          <div className={styles.dividerLine} />
          <div className={styles.summaryGrid}>
            <Typography variant='body-s' className={styles.pageSummary}>
              This page shows all metrics from the Beneficial Microbes,
              Disruptive Microbes, Balance and Robustness, and Digestion
              sections of your report.
            </Typography>
            <EvaluationGroupsSummary
              metrics={submetrics}
              variant='both'
              includedByFilter={isIncludedByFilter}
            />
          </div>
        </div>
        <CategoryMetricsFilter
          submetrics={submetrics}
          setFiltered={setEvaluationFilters}
        />
      </div>

      <div className={styles.cardsContainer}>
        {groupedSubMetrics.map(category => {
          const emptyCategory = !category.orderedMetrics.some(
            metric =>
              metric.orderedSubmetrics.filter(
                submetric => submetric.includedByFilters,
              ).length,
          );

          const categoryCounts = {
            green: 0,
            orange: 0,
            grey: 0,
            red: 0,
          };

          category.orderedMetrics.forEach(metric => {
            metric.orderedSubmetrics
              .filter(submetric => submetric.includedByFilters)
              .forEach(submetric => {
                if (submetric.evaluation_color === 'green')
                  categoryCounts.green++;
                else if (submetric.evaluation_color === 'orange')
                  categoryCounts.orange++;
                else if (submetric.evaluation_color === 'grey')
                  categoryCounts.grey++;
                else if (submetric.evaluation_color === 'red')
                  categoryCounts.red++;
              });
          });

          return (
            <Accordion
              key={category.name} // Use stable category name as key
              isOpen={accordionOpenStates[category.name] ?? !emptyCategory} // Control state from component
              onToggle={isOpen => handleAccordionToggle(category.name, isOpen)} // Handle toggle
              header={
                <div className='flex justify-between w-100 gap-2 pr-4'>
                  <Typography
                    variant='heading-l'
                    className={styles.categoryTitle}
                  >
                    {category.name}
                  </Typography>
                  <div className='flex gap-1'>
                    <SingleMetric
                      count={categoryCounts.green}
                      color='green'
                      styleClass={styles.green}
                    />
                    <SingleMetric
                      count={categoryCounts.orange}
                      color='orange'
                      styleClass={styles.orange}
                    />
                    <SingleMetric
                      count={categoryCounts.grey}
                      color='grey'
                      styleClass={styles.grey}
                    />
                    <SingleMetric
                      count={categoryCounts.red}
                      color='red'
                      styleClass={styles.red}
                    />
                  </div>
                </div>
              }
              className={styles.metricAccordion}
            >
              {emptyCategory && (
                <>
                  <Typography variant='body-s' className={styles.emptyCategory}>
                    No metrics matching your active filters.
                  </Typography>
                </>
              )}
              {category.orderedMetrics.map(metric => {
                const submetrics = metric.orderedSubmetrics.filter(
                  submetric => submetric.includedByFilters,
                );

                if (!submetrics?.length) return <></>;
                return (
                  <div
                    key={`metric_${metric.name}`}
                    className={styles.categoryMetric}
                  >
                    <Typography
                      variant='heading-s'
                      className={styles.metricTitle}
                    >
                      {metric.name}
                    </Typography>
                    <MetricCard key={`card_${metric.name}`}>
                      {submetrics.map(subMetric => (
                        <SubMetricRow
                          key={`${metric.name}_${subMetric.name}`}
                          kitId={currentKitId}
                          subMetric={subMetric}
                        />
                      ))}
                    </MetricCard>
                  </div>
                );
              })}
            </Accordion>
          );
        })}
      </div>
    </PageContainer>
  );
};
